// Central data source for the Amden Ecosystem catalogue.
// One typed, CMS/CRM-ready model for systems, services, training and partner
// solutions. Presentation logic lives in components.
//
// ──────────────────────────────────────────────────────────────────────────
import { getLive } from '@/lib/liveContent';
// DEVELOPER NOTE — selective e-commerce
// `purchaseMode`, `billingType`, `price`, `depositAmount`, `vatRate`,
// `stripeProductId`, `stripePriceId` etc. are CONFIGURATION ONLY.
// Payment is NOT active: `price` stays null and catalogue CTAs render
// "View Details" / enquiry actions until scope, price, VAT, delivery,
// cancellation/refund terms and a valid Stripe product are approved.
//
// CRM handoff (future): every purchase/enquiry can carry customer + company
// details, crmProductCode, itemType, serviceDivision, purchaseMode, amount +
// VAT, payment status, source, Stripe identifiers, partner source and
// terms-acceptance timestamp through the secure backend service layer.
// ──────────────────────────────────────────────────────────────────────────

export const ECOSYSTEM_SEO = {
  title: 'Business Solutions Catalogue | Amden Group',
  description:
    'Explore Amden systems, productised services, training and partner solutions in one catalogue — across operations, people, growth and business launch.',
};

export const ECOSYSTEM_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'operations', label: 'Operations' },
  { key: 'people', label: 'People' },
  { key: 'growth', label: 'Growth' },
  { key: 'launch', label: 'Launch' },
  { key: 'systems', label: 'Systems' },
  { key: 'partner', label: 'Partner Solutions' },
  { key: 'training', label: 'Training' },
  { key: 'fixed-price', label: 'Fixed-Price Services' },
];

export const DIVISION_COLORS = {
  operations: { label: 'Amden Operations', color: '#FFB800', soft: '#FFF3CC', dark: '#9C7400' },
  people: { label: 'Amden People', color: '#D6318B', soft: '#FBDCEE', dark: '#A01E66' },
  growth: { label: 'Amden Growth', color: '#029E88', soft: '#CCF2EC', dark: '#01755F' },
  launch: { label: 'Amden Launch', color: '#4225C4', soft: '#E4E0FA', dark: '#331BA0' },
  amden: { label: 'Amden Group', color: '#0B1729', soft: '#E2E8F2', dark: '#0B1729' },
};

export const ITEM_TYPE_LABELS = {
  amden_service: 'Amden Service',
  amden_system: 'Amden System',
  training: 'Training',
  affiliate_product: 'Affiliate Product',
  partner_service: 'Partner Service',
  external_platform: 'External Platform',
};

export const RELATIONSHIP_LABELS = {
  amden: 'Amden',
  partner: 'Partner',
  affiliate: 'Affiliate',
};

// Optional editorial badges for selected catalogue items (e.g. priority
// services). null = no badge.
export const BADGE_LABELS = {
  new: 'New',
  popular: 'Popular',
};

// Current catalogue action per purchase mode. No payment is active:
// purchase-capable modes render "View Details" until approved + configured.
export const PURCHASE_ACTIONS = {
  direct_purchase: { label: 'View Details', kind: 'details' },
  book_and_pay: { label: 'View Details', kind: 'details' },
  pay_deposit: { label: 'View Details', kind: 'details' },
  subscription_application: { label: 'Book a Consultation', kind: 'enquiry' },
  submit_requirements: { label: 'Submit a Vacancy', kind: 'enquiry' },
  request_quote: { label: 'Request a Quote', kind: 'enquiry' },
  book_consultation: { label: 'Book a Consultation', kind: 'enquiry' },
  contact_us: { label: 'Contact Amden', kind: 'enquiry' },
  external_purchase: { label: 'Visit Partner', kind: 'external' },
  affiliate_link: { label: 'Visit Partner', kind: 'external' },
};

const BASE = {
  provider: 'Amden Group',
  suitableFor: null,
  format: null,
  price: null,
  depositAmount: null,
  vatRate: null,
  priceDisplay: null,
  stripeProductId: null,
  stripePriceId: null,
  termsUrl: null,
  refundPolicyUrl: null,
  deliveryTimescale: null,
  requiresConsultation: false,
  requiresOnboarding: false,
  requiresTermsAcceptance: false,
  availableQuantity: null,
  bookingRequired: false,
  externalCheckoutUrl: null,
  crmProductCode: null,
  relationship: 'amden',
  availability: 'available',
  badge: null,
};

const item = (fields) => ({ ...BASE, ...fields });

export const ECOSYSTEM_ITEMS = [
  // ── Amden Systems ──────────────────────────────────────────────────────
  item({
    id: 'eco-compass', slug: 'amden-compass', name: 'Amden Compass',
    itemType: 'amden_system', serviceDivision: 'operations',
    shortDescription: 'Business planning, coaching, actions and performance visibility in one structured management platform.',
    format: 'Cloud platform', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['systems', 'operations'], displayOrder: 1,
    href: '/systems/compass',
    badge: 'popular',
  }),
  item({
    id: 'eco-pulse', slug: 'amden-pulse', name: 'Amden Pulse',
    itemType: 'amden_system', serviceDivision: 'operations',
    shortDescription: 'Workforce timesheets, approvals, leave, compliance, labour costs and site and customer profitability in one connected platform.',
    format: 'Cloud platform', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['systems', 'operations', 'people'], displayOrder: 2,
    href: '/systems/pulse',
    badge: 'popular',
  }),
  item({
    id: 'eco-foresee', slug: 'amden-foresee', name: 'Amden Foresee',
    itemType: 'amden_system', serviceDivision: 'operations',
    shortDescription: 'Frontline reporting, actions, compliance visibility, contractor management and multi-site EHSQ oversight in one connected platform.',
    format: 'Cloud platform', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['systems', 'operations'], displayOrder: 3,
    href: '/systems/foresee',
    badge: 'popular',
  }),
  item({
    id: 'eco-reach', slug: 'amden-reach', name: 'Amden Reach',
    itemType: 'amden_system', serviceDivision: 'operations',
    shortDescription: 'Customers, pipeline, follow-ups, referrals and sales reporting in one practical CRM — with an AI assistant grounded in your data.',
    format: 'Cloud platform', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['systems', 'operations'], displayOrder: 4,
    href: '/amden-reach',
    badge: 'popular',
  }),

  // ── People & Recruitment ────────────────────────────────────────────────
  item({
    id: 'eco-recruitment-retainer', slug: 'recruitment-retainer', name: 'Recruitment Retainer',
    itemType: 'amden_service', serviceDivision: 'people',
    shortDescription: 'An ongoing recruitment-support model for businesses with regular hiring requirements.',
    suitableFor: 'Growing businesses, multi-role recruitment and employers requiring continuous recruitment support.',
    format: 'Ongoing monthly support', purchaseMode: 'book_consultation', billingType: 'monthly',
    requiresConsultation: true, requiresOnboarding: true, requiresTermsAcceptance: true,
    areas: ['people'], displayOrder: 10,
  }),
  item({
    id: 'eco-pay-per-placement', slug: 'pay-per-placement', name: 'Pay per Placement',
    itemType: 'amden_service', serviceDivision: 'people',
    shortDescription: 'A flexible recruitment service where Amden supports an individual vacancy and the agreed fee becomes payable when a candidate is successfully placed.',
    suitableFor: 'Businesses with occasional or one-off recruitment needs.',
    format: 'Per-vacancy recruitment support', purchaseMode: 'submit_requirements', billingType: 'per_vacancy',
    requiresConsultation: true, requiresOnboarding: true, requiresTermsAcceptance: true,
    areas: ['people'], displayOrder: 11,
  }),
  item({
    id: 'eco-hr-packs', slug: 'hr-packs', name: 'HR Packs',
    itemType: 'amden_service', serviceDivision: 'people',
    shortDescription: 'Defined HR documentation packages designed around the size and requirements of the business.',
    format: 'Defined documentation package', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['people', 'fixed-price'], displayOrder: 12,
  }),
  item({
    id: 'eco-everything-disc', slug: 'everything-disc-profiles', name: 'Everything DiSC® Profiles',
    itemType: 'amden_service', serviceDivision: 'people',
    shortDescription: 'Individual profiles and facilitated options supporting communication, working relationships and team development.',
    format: 'Profile with facilitated session options', purchaseMode: 'direct_purchase', billingType: 'per_profile',
    areas: ['people', 'fixed-price'], displayOrder: 13,
  }),
  item({
    id: 'eco-team-training', slug: 'team-training', name: 'Team Training',
    itemType: 'training', serviceDivision: 'people',
    shortDescription: 'Practical team-development sessions covering communication, collaboration, accountability and performance.',
    format: 'Facilitated session', purchaseMode: 'book_and_pay', billingType: 'per_person',
    bookingRequired: true, areas: ['people', 'training', 'fixed-price'], displayOrder: 14,
  }),
  item({
    id: 'eco-leadership-training', slug: 'leadership-management-training', name: 'Leadership and Management Training',
    itemType: 'training', serviceDivision: 'people',
    shortDescription: 'Practical development for current and emerging managers.',
    format: 'Development programme', purchaseMode: 'book_and_pay', billingType: 'per_person',
    bookingRequired: true, areas: ['people', 'training', 'fixed-price'], displayOrder: 15,
  }),

  // ── Operations services ─────────────────────────────────────────────────
  item({
    id: 'eco-operational-review', slug: 'operational-review', name: 'Operational Review',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'A structured review of how the business operates day to day, identifying inefficiencies, risks and priorities.',
    format: 'Fixed-scope review', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['operations', 'fixed-price'], displayOrder: 20,
  }),
  item({
    id: 'eco-process-mapping-workshop', slug: 'process-mapping-workshop', name: 'Process-Mapping Workshop',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'A facilitated workshop that maps how work actually flows and where it can be improved.',
    format: 'Facilitated workshop', purchaseMode: 'direct_purchase', billingType: 'one_off',
    bookingRequired: true, areas: ['operations', 'fixed-price'], displayOrder: 21,
  }),
  item({
    id: 'eco-kpi-reporting-review', slug: 'kpi-reporting-review', name: 'KPI and Reporting Review',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'A focused review of your measures, reports and management rhythms — what to track and how to use it.',
    format: 'Fixed-scope review', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['operations', 'fixed-price'], displayOrder: 22,
  }),
  item({
    id: 'eco-systems-assessment', slug: 'systems-assessment', name: 'Systems Assessment',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'An assessment of your current software and information flow, with clear recommendations on what to keep, connect or replace.',
    format: 'Fixed-scope assessment', purchaseMode: 'request_quote', billingType: 'custom_quote',
    areas: ['operations', 'systems'], displayOrder: 23,
  }),
  item({
    id: 'eco-crm-setup', slug: 'crm-setup', name: 'CRM Setup',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'Practical CRM selection, configuration and rollout around the way your business actually works.',
    format: 'Scoped implementation', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, requiresOnboarding: true, areas: ['operations', 'systems'], displayOrder: 24,
  }),
  item({
    id: 'eco-workflow-automation-review', slug: 'workflow-automation-review', name: 'Workflow Automation Review',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'A review of repetitive manual administration and where sensible automation would save time.',
    format: 'Fixed-scope review', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['operations', 'fixed-price'], displayOrder: 25,
  }),
  item({
    id: 'eco-management-structure-review', slug: 'management-structure-review', name: 'Management Structure Review',
    itemType: 'amden_service', serviceDivision: 'operations',
    shortDescription: 'A review of roles, accountability and meeting rhythms to give management clearer control.',
    format: 'Fixed-scope review', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['operations', 'fixed-price'], displayOrder: 26,
  }),

  // ── Growth services ─────────────────────────────────────────────────────
  item({
    id: 'eco-marketing-review', slug: 'marketing-review', name: 'Marketing Review',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A practical review of your current marketing activity, channels and priorities.',
    format: 'Fixed-scope review', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['growth', 'fixed-price'], displayOrder: 30,
  }),
  item({
    id: 'eco-website-audit', slug: 'website-audit', name: 'Website Audit',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A structured audit of your website’s credibility, usability, visibility and enquiry journey.',
    format: 'Fixed-scope audit', purchaseMode: 'direct_purchase', billingType: 'one_off',
    areas: ['growth', 'fixed-price'], displayOrder: 31,
  }),
  item({
    id: 'eco-social-media-planning', slug: 'social-media-planning-session', name: 'Social Media Planning Session',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A working session to build a structured, realistic social media plan around your business goals.',
    format: 'Facilitated session', purchaseMode: 'book_and_pay', billingType: 'one_off',
    bookingRequired: true, areas: ['growth', 'fixed-price'], displayOrder: 32,
  }),
  item({
    id: 'eco-marketing-strategy-workshop', slug: 'marketing-strategy-workshop', name: 'Marketing Strategy Workshop',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A facilitated workshop producing a focused marketing strategy: audiences, messages, channels and measures.',
    format: 'Facilitated workshop', purchaseMode: 'book_and_pay', billingType: 'one_off',
    bookingRequired: true, areas: ['growth', 'fixed-price'], displayOrder: 33,
  }),
  item({
    id: 'eco-website-support', slug: 'website-support', name: 'Website Support',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'Ongoing website improvement covering content, usability, visibility and lead generation.',
    format: 'Ongoing support', purchaseMode: 'request_quote', billingType: 'monthly',
    requiresConsultation: true, areas: ['growth'], displayOrder: 34,
  }),
  item({
    id: 'eco-landing-page-package', slug: 'landing-page-package', name: 'Landing-Page Package',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A focused landing page designed around a single offer, campaign or audience.',
    format: 'Scoped project', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['growth'], displayOrder: 35,
  }),
  item({
    id: 'eco-marketing-training', slug: 'marketing-training', name: 'Marketing Training',
    itemType: 'training', serviceDivision: 'growth',
    shortDescription: 'Practical training that helps owners and teams plan content, use channels effectively and measure results.',
    format: 'Facilitated session', purchaseMode: 'book_and_pay', billingType: 'per_person',
    bookingRequired: true, areas: ['growth', 'training', 'fixed-price'], displayOrder: 36,
  }),
  item({
    id: 'eco-ongoing-social-media', slug: 'ongoing-social-media-support', name: 'Ongoing Social Media Support',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'Structured ongoing support to keep your social media consistent, professional and commercially focused.',
    format: 'Ongoing monthly support', purchaseMode: 'book_consultation', billingType: 'monthly',
    requiresConsultation: true, areas: ['growth'], displayOrder: 37,
  }),
  item({
    id: 'eco-website-development', slug: 'website-development', name: 'Website Development',
    itemType: 'amden_service', serviceDivision: 'growth',
    shortDescription: 'A professional website structured around credibility, customer journeys, enquiries and future growth.',
    format: 'Scoped project', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, requiresOnboarding: true, areas: ['growth'], displayOrder: 38,
  }),

  // ── Launch services ─────────────────────────────────────────────────────
  item({
    id: 'eco-business-idea-review', slug: 'business-idea-review', name: 'Business Idea Review',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'A structured session to examine the concept, target customer, commercial assumptions, risks and practical next steps.',
    format: 'Facilitated session', purchaseMode: 'direct_purchase', billingType: 'one_off',
    bookingRequired: true, areas: ['launch', 'fixed-price'], displayOrder: 40,
  }),
  item({
    id: 'eco-business-planning-session', slug: 'business-planning-session', name: 'Business Planning Session',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'A working session to shape a usable business plan, commercial roadmap and launch priorities.',
    format: 'Facilitated session', purchaseMode: 'book_and_pay', billingType: 'one_off',
    bookingRequired: true, areas: ['launch', 'fixed-price'], displayOrder: 41,
  }),
  item({
    id: 'eco-launch-strategy', slug: 'launch-strategy', name: 'Launch Strategy',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'A structured strategy covering the offer, audience, positioning, route to market and launch actions.',
    format: 'Scoped engagement', purchaseMode: 'request_quote', billingType: 'custom_quote',
    areas: ['launch'], displayOrder: 42,
  }),
  item({
    id: 'eco-brand-starter', slug: 'brand-starter-package', name: 'Brand Starter Package',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'Core brand positioning, visual identity and messaging foundations for a professional launch.',
    format: 'Scoped package', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['launch'], displayOrder: 43,
  }),
  item({
    id: 'eco-business-website-package', slug: 'business-website-package', name: 'Business Website Package',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'A professional launch website built around credibility, customer journeys and enquiries.',
    format: 'Scoped package', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['launch'], displayOrder: 44,
  }),
  item({
    id: 'eco-systems-starter-package', slug: 'systems-starter-package', name: 'Systems Starter Package',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'Practical CRM, workflow, reporting and administration foundations for a more organised launch.',
    format: 'Scoped package', purchaseMode: 'request_quote', billingType: 'custom_quote',
    requiresConsultation: true, areas: ['launch', 'systems'], displayOrder: 45,
  }),
  item({
    id: 'eco-full-launch-programme', slug: 'full-business-launch-programme', name: 'Full Business Launch Programme',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'Joined-up support covering planning, structure, brand, website, systems and launch execution.',
    format: 'End-to-end programme', purchaseMode: 'book_consultation', billingType: 'custom_quote',
    requiresConsultation: true, requiresOnboarding: true, requiresTermsAcceptance: true,
    areas: ['launch'], displayOrder: 46,
  }),
  item({
    id: 'eco-post-launch-support', slug: 'post-launch-support', name: 'Post-Launch Support',
    itemType: 'amden_service', serviceDivision: 'launch',
    shortDescription: 'Ongoing practical support to review performance, solve early operational issues and build momentum.',
    format: 'Ongoing support', purchaseMode: 'contact_us', billingType: 'monthly',
    requiresConsultation: true, areas: ['launch'], displayOrder: 47,
  }),
].map((it) => ({ ...it, href: `/ecosystem/services/${it.slug}` }));

// Live CMS getter — published listings from the database are the source of
// truth after migration; ECOSYSTEM_ITEMS remains the logged fallback.
export const getEcosystemItems = () => getLive('listings') || ECOSYSTEM_ITEMS;

export const ECOSYSTEM_HEADER = {
  eyebrow: 'Amden Ecosystem',
  heading: 'Every business solution, in one place',
  intro:
    'Systems, productised services, training and partner solutions across operations, people, growth and business launch — discover the right support and the right way to buy it.',
};

export const ECOSYSTEM_NOTE = {
  heading: 'How purchasing works',
  text: 'Some services have a fixed scope and will be available to buy or book directly. Bespoke work — consultancy, recruitment assignments, websites, systems and launch programmes — always starts with a conversation, so the scope is right before anything is agreed.',
};

export const ECOSYSTEM_CTA = {
  index: '03',
  eyebrow: 'Next step',
  heading: 'Not sure which solution fits your business?',
  copy: 'Tell us what you’re trying to achieve and we’ll point you towards the right service, system or support.',
  primaryLabel: 'Discuss Your Needs',
  primaryHref: '/?source=Ecosystem#solution-finder',
  secondaryLabel: 'Explore Services',
  secondaryHref: '/services',
};
